import React, { useContext, useMemo, useState } from 'react';
import BlankButton from '@xo-union/component-blank-button';
import Icon from '@xo-union/tk-component-icons';
import { IconButton } from '../../IconButton';
import { ConfirmationModal } from '../../../libs/gs-component-form';
import { DisplayButton } from '@xo-union/tk-ui-links';
import { H4, Body1 } from '@xo-union/tk-ui-typography';
import { useHistory } from 'react-router-dom';
import { compose } from '@xo-union/react-css-modules';

import StatusText from './StatusText';
import { ButtonDropdown, ButtonDropdownItem } from '../../ButtonDropdown';
import { rsvpSettingsRoute } from '../../../helpers/routes';
import styles from './header.scss';
import { useWebsiteLayoutContext } from '../../../context/WebsiteLayoutContext';
import { Link } from '@xo-union/tk-component-buttons';
import { useMobileMedia } from '@xo-union/tk-ui-breakpoints';
import classNames from 'classnames';
import { TrackingContext } from '../../../TrackingContext';
import { useGetHotelEngineUrl } from '../../../hooks/useGetHotelEngineUrl';

interface Props {
  titleText: string;
  statusText?: string;
  open?: boolean;
  toggleOpen?: () => void;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  previewOnClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  subHeadOnClick?: (event: React.MouseEvent<HTMLButtonElement>) => Promise<void>;
  guidance?: boolean;
  hasDropdown?: boolean;
  subHeadText?: string;
  toggleVisibility?: (showMessage?: boolean, messageInfo?: string) => void;
  removePage?: (() => Promise<void>) | null;
  rsvpPage?: boolean;
  pageType?: string;
  customPage?: boolean;
  as?: string; // What to render the title text H4 component as, if you want it to be something other than "h4".
}

interface DropdownProps {
  toggleVisibility?: () => void;
  statusText?: string;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  removePage?: (() => Promise<void>) | null;
}

const PageDropdownItems = ({ toggleVisibility, statusText, onClick, removePage }: DropdownProps) => {
  const { currentLayout } = useWebsiteLayoutContext();

  const isSinglePageSite = currentLayout === 'single-page';
  const pageOrSection = isSinglePageSite ? 'Section' : 'Page';

  const HIDE_TEXT = `Hide ${pageOrSection}`;
  const SHOW_TEXT = `Show ${pageOrSection}`;

  return (
    <>
      <ButtonDropdownItem
        data-testid={statusText === 'Hidden' ? 'show-page' : 'hide-page'}
        key="make-visible"
        onClick={toggleVisibility}
        title={statusText === 'Hidden' ? SHOW_TEXT : HIDE_TEXT}
        icon={statusText === 'Hidden' ? 'eye' : 'eye_closed'}
        subText=""
        classes={compose({
          buttonDropdownItem: styles.buttonItem,
          textContainer: styles.buttonText,
        })}
      />
      <ButtonDropdownItem
        data-testid="rename-page"
        key="rename-page"
        onClick={(e): void => onClick && onClick(e as any)}
        title={`Rename ${pageOrSection}`}
        icon="edit"
        subText=""
        classes={compose({
          buttonDropdownItem: styles.buttonItem,
          textContainer: styles.buttonText,
        })}
      />
      {!!removePage && (
        <ButtonDropdownItem
          data-testid="remove-page"
          key="remove-page"
          onClick={removePage}
          title={`Remove ${pageOrSection}`}
          icon="delete"
          subText=""
          classes={compose({
            buttonDropdownItem: styles.buttonItem,
            textContainer: styles.buttonText,
            iconTextContainer: styles.redText,
          })}
        />
      )}
    </>
  );
};

const RsvpDropdownItems = ({ statusText, toggleVisibility, onClick }: DropdownProps) => {
  const history = useHistory();
  const { currentLayout } = useWebsiteLayoutContext();

  const isSinglePageSite = currentLayout === 'single-page';
  const pageOrSection = isSinglePageSite ? 'Section' : 'Page';

  const HIDE_TEXT = `Hide ${pageOrSection}`;
  const SHOW_TEXT = `Show ${pageOrSection}`;

  return (
    <>
      {statusText !== 'Hidden' && (
        <ButtonDropdownItem
          data-testid="edit-form"
          key="edit-form"
          onClick={(): void => history.push(rsvpSettingsRoute)}
          title="Edit Form"
          icon="edit"
          subText=""
          classes={compose({
            buttonDropdownItem: styles.buttonItem,
            textContainer: styles.buttonText,
          })}
        />
      )}
      <ButtonDropdownItem
        data-testid="make-rsvp-visible"
        key="make-rsvp-visible"
        onClick={toggleVisibility}
        title={statusText === 'Hidden' ? SHOW_TEXT : HIDE_TEXT}
        icon={statusText === 'Hidden' ? 'eye' : 'eye_closed'}
        subText=""
        classes={compose({
          buttonDropdownItem: styles.buttonItem,
          textContainer: styles.buttonText,
        })}
      />
      {
        <ButtonDropdownItem
          data-testid="rsvp-settings"
          key="rsvp-settings"
          onClick={(e): void => onClick && onClick(e as any)}
          title="RSVP Settings"
          icon="settings"
          subText=""
          classes={compose({
            buttonDropdownItem: styles.buttonItem,
            textContainer: styles.buttonText,
          })}
        />
      }
    </>
  );
};

interface BookHotelsLinkProps {
  pageType?: string;
  isMobile: boolean;
}

const BookHotelsLink = ({ pageType, isMobile }: BookHotelsLinkProps) => {
  const { getHotelEngineUrl } = useGetHotelEngineUrl();
  const hotelEngineUrl = getHotelEngineUrl();
  const { track } = useContext(TrackingContext);

  if (pageType !== 'TravelPage') {
    return null;
  }

  const onClickLink = () => {
    track('Item Action Initiated', {
      action: 'hotel_added',
      actionName: 'hotel_added_link',
    });
  };

  return (
    <Link
      color="secondary-alternative"
      href={hotelEngineUrl}
      className={classNames(styles.hotelBlocksLinkButton, { [styles.hotelBlocksLinkButton__mobile]: isMobile })}
      target="_blank"
      rel="sponsored"
      onClick={onClickLink}
    >
      Book hotels
    </Link>
  );
};

export const Header: React.FC<Props> = ({
  titleText,
  statusText,
  onClick,
  previewOnClick,
  subHeadOnClick,
  open,
  toggleOpen,
  guidance,
  subHeadText,
  toggleVisibility,
  removePage,
  hasDropdown,
  rsvpPage = false,
  customPage = false,
  pageType,
  as = '',
}) => {
  const { currentLayout } = useWebsiteLayoutContext();

  const isMobile = !!useMobileMedia()?.yes;

  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const DropdownItems = rsvpPage ? RsvpDropdownItems : PageDropdownItems;
  const subHeadTextIntro = useMemo(() => (subHeadText ? `${subHeadText.replace(/\.$/, '')}, or ` : ''), [subHeadText]);
  const isSubHeadText = subHeadText || typeof subHeadOnClick === 'function';

  const isSinglePageSite = currentLayout === 'single-page';
  const pageOrSection = isSinglePageSite ? 'Section' : 'Page';
  const lowerCasePageOrSuggestion = pageOrSection.toLowerCase();

  const handleRemovePage = () => {
    setDeleteConfirmationOpen(!deleteConfirmationOpen);
  };

  const toggleVisabilityHelper = () => {
    if (removePage) {
      handleRemovePage();
    } else if (toggleVisibility) {
      const showMessage: boolean = !customPage && !!guidance;
      toggleVisibility(showMessage, `To add ${titleText}, scroll down to Suggested Pages.`);
    }
  };
  const renderSubHeadText = () => (
    <>
      {subHeadTextIntro}
      <DisplayButton data-testid="cardheader-subhead-action" onClick={subHeadOnClick} className={styles.comingSoonLink}>
        {subHeadText ? 'add with "coming soon"' : 'Add with "coming soon"'}
      </DisplayButton>
    </>
  );

  return (
    <div className={styles.Header__Container}>
      <div className={styles.flex} data-testid="flex-container">
        <div className={styles.leftContentContainer}>
          {toggleOpen && typeof open === 'boolean' ? (
            <Icon
              data-testid="caret_icon"
              name={open ? 'caret_down' : 'caret_right'}
              onClick={toggleOpen}
              size="md"
              className={styles.caretIcon}
            />
          ) : null}
          <BlankButton onClick={onClick} className={styles.blankButton}>
            <H4 className={`${styles.titleText} ${statusText === 'Hidden' && styles.hidden}`} as={as}>
              {guidance ? `Add ${titleText}` : titleText}
            </H4>
            {statusText && statusText !== 'Visible' && (
              <StatusText className={styles.headerStatus} data-testid="header-status-text">
                <span className={styles.headerStatusText}>
                  {statusText === 'Hidden' ? <Icon name="eye_closed" size="sm" className={styles.hiddenIcon} /> : null}
                </span>
              </StatusText>
            )}
          </BlankButton>
        </div>

        {!isMobile && <BookHotelsLink pageType={pageType} isMobile={isMobile} />}

        {guidance && (
          <IconButton
            name="close"
            size="sm"
            className={styles.maybeLater}
            datatestid="delete-icon"
            arialabel='Remove "Add Page" suggestion'
            onClick={toggleVisabilityHelper} // eslint-disable-line react/jsx-handler-names
          />
        )}
        <ConfirmationModal
          open={deleteConfirmationOpen}
          headerText="Are you sure?"
          bodyText={`This will permanently remove the ${lowerCasePageOrSuggestion} from your Website.`}
          cancelText="No, Cancel"
          confirmText="Yes, Continue"
          onConfirm={removePage ? removePage : () => {}}
          onCancel={() => setDeleteConfirmationOpen(false)}
        />
        {!guidance && previewOnClick && (
          <>
            <DisplayButton onClick={previewOnClick} color="primary">
              {titleText === 'Home' ? 'Preview' : 'Edit'}
            </DisplayButton>
            {hasDropdown && <span className={styles.verticalLine} />}
          </>
        )}
        {!guidance && hasDropdown && (
          <ButtonDropdown
            size="md"
            color="tertiary"
            text=""
            icon="more_filled"
            className={styles.buttonDropdown}
            listClassName={styles.buttonList}
          >
            <DropdownItems
              toggleVisibility={toggleVisibility}
              statusText={statusText}
              onClick={onClick}
              removePage={removePage}
            />
          </ButtonDropdown>
        )}
      </div>

      {isMobile && <BookHotelsLink pageType={pageType} isMobile={isMobile} />}

      {guidance && open !== false && isSubHeadText && (
        <Body1 className={open ? styles.shiftedSubHeader : styles.subHeader}>
          {customPage ? subHeadText : renderSubHeadText()}
        </Body1>
      )}
    </div>
  );
};
